import type { CreateInvitationRequest } from '@noconflict/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../shared/api/supabase';
import {
  acceptInvitation,
  acceptMyInvitation,
  createInvitation,
  getInvitation,
  listInvitations,
  listMyPendingInvitations,
  resendInvitation,
  revokeInvitation,
} from './api';

export const invitationKeys = {
  org: (organizationId: string) => ['invitations', organizationId] as const,
  mine: () => ['invitations', 'mine'] as const,
  lookup: (token: string) => ['invitations', 'lookup', token] as const,
};

async function sessionToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('forbidden');
  return token;
}

export function useInvitations(organizationId: string, enabled = true) {
  return useQuery({
    queryKey: invitationKeys.org(organizationId),
    queryFn: () => listInvitations(supabase, organizationId),
    enabled,
  });
}

export function useInvitationLookup(token: string) {
  return useQuery({
    queryKey: invitationKeys.lookup(token),
    queryFn: () => getInvitation(supabase, token),
    retry: false,
  });
}

export function useMyPendingInvitations(enabled = true) {
  return useQuery({
    queryKey: invitationKeys.mine(),
    queryFn: () => listMyPendingInvitations(supabase),
    enabled,
  });
}

/**
 * Invitations are identity mutations: accepting one changes what the caller can see, so the org scope is
 * refreshed along with the lists. A failed create still refreshes, because the invitation may exist even
 * though its email did not go out.
 */
function useInvitationMutation<TInput, TResult>(
  organizationId: string | null,
  mutationFn: (input: TInput) => Promise<TResult>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({
          queryKey: organizationId ? invitationKeys.org(organizationId) : ['invitations'],
        }),
        qc.invalidateQueries({ queryKey: ['org-scope'] }),
      ]),
  });
}

export function useCreateInvitation(organizationId: string) {
  return useInvitationMutation(organizationId, async (input: CreateInvitationRequest) =>
    createInvitation(await sessionToken(), organizationId, input),
  );
}

export function useResendInvitation(organizationId: string) {
  return useInvitationMutation(organizationId, async (invitationId: string) =>
    resendInvitation(await sessionToken(), organizationId, invitationId),
  );
}

export function useRevokeInvitation(organizationId: string) {
  return useInvitationMutation(organizationId, (invitationId: string) =>
    revokeInvitation(supabase, invitationId),
  );
}

export function useAcceptInvitation() {
  return useInvitationMutation(null, (token: string) => acceptInvitation(supabase, token));
}

export function useAcceptMyInvitation() {
  return useInvitationMutation(null, (invitationId: string) =>
    acceptMyInvitation(supabase, invitationId),
  );
}
